import { supabase } from './supabase'
import { validateIban } from './validators/iban'

/**
 * The bills that come every month anyway.
 *
 * A rule stores an RRULE string and nothing tries to interpret it here — the
 * worker materialises real instalments sixty days ahead (spec.md §10), so the
 * calendar, the totals and the payment runs all work on ordinary rows with no
 * special cases. This file only creates, lists and stops rules.
 *
 * The distinction that matters is fixed versus variable. Rent is fixed: you
 * typed the amount and the account, which is verification, and it can be paid
 * without a screen every month. Electricity is variable: what appears is a
 * median of its own history, marked as an estimate, and the database refuses
 * to pay it until the real invoice arrives.
 */

export type AmountMode = 'fixed' | 'variable'

/**
 * The database enum, mirrored. It matters that this list is exact: a value
 * that is not in it is refused at insert time, which is where 0022 moved the
 * failure to — it used to surface as "the rent never appears".
 */
export type CollectionMethod =
  | 'manual'
  | 'direct_debit'
  | 'standing_order'
  | 'card'
  | 'cash'

export const COLLECTION_LABELS: Record<CollectionMethod, string> = {
  manual: 'Ik betaal het zelf',
  direct_debit: 'Domiciliëring',
  standing_order: 'Doorlopende opdracht',
  card: 'Kaart',
  cash: 'Contant',
}

export interface RecurringRule {
  id: string
  spaceId: string
  partyId: string | null
  description: string
  category: string | null
  rrule: string
  startsOn: string
  endsOn: string | null
  mode: AmountMode
  amountCents: number
  iban: string | null
  paymentReference: string | null
  /** One of the database's own values — never the word "transfer" (0022). */
  collectionMethod: CollectionMethod
  active: boolean
  lastMaterialisedOn: string | null
}

/**
 * The shapes real household bills actually take. Anything else would need an
 * RRULE editor, and an RRULE editor is a thing nobody has ever enjoyed using.
 */
export interface Cadence {
  key: string
  label: string
  /**
   * `dayOfMonth` is the day chosen; `month` (1-12) is only used by the yearly
   * shape, and it is not optional there — see the warning below.
   */
  build: (dayOfMonth: number, month: number) => string
}

/**
 * The shapes real household bills actually take. Anything else would need an
 * RRULE editor, and an RRULE editor is a thing nobody has ever enjoyed using.
 *
 * **The yearly one carries a scar.** It used to read `FREQ=YEARLY;BYMONTHDAY=d`
 * with no month, which reads like "once a year on the dth" and is not what
 * RRULE means: without BYMONTH, a yearly rule falls back to every month that
 * matches, so it produces **twelve invoices a year instead of one**. The
 * nightly job would have created eleven bills nobody owed, quietly, for as long
 * as the rule lived. Measured, not guessed: `between()` over 2026 returns 12
 * without BYMONTH and 1 with it. 0037 repairs any rule already stored this way.
 */
export const CADENCES: Cadence[] = [
  { key: 'monthly', label: 'Elke maand', build: (d) => `FREQ=MONTHLY;BYMONTHDAY=${d}` },
  { key: 'quarterly', label: 'Elk kwartaal', build: (d) => `FREQ=MONTHLY;INTERVAL=3;BYMONTHDAY=${d}` },
  { key: 'half', label: 'Elke zes maanden', build: (d) => `FREQ=MONTHLY;INTERVAL=6;BYMONTHDAY=${d}` },
  {
    key: 'yearly',
    label: 'Elk jaar',
    build: (d, month) => `FREQ=YEARLY;BYMONTH=${month};BYMONTHDAY=${d}`,
  },
]

const MONTHS = [
  'januari', 'februari', 'maart', 'april', 'mei', 'juni',
  'juli', 'augustus', 'september', 'oktober', 'november', 'december',
]

export function describeCadence(rrule: string): string {
  const day = /BYMONTHDAY=(\d+)/.exec(rrule)?.[1]
  const on = day ? ` op de ${day}e` : ''
  if (rrule.includes('INTERVAL=3')) return `Elk kwartaal${on}`
  if (rrule.includes('INTERVAL=6')) return `Elke zes maanden${on}`
  if (rrule.includes('FREQ=YEARLY')) {
    const month = /BYMONTH=(\d+)/.exec(rrule)?.[1]
    const name = month ? MONTHS[Number(month) - 1] : null
    return name && day ? `Elk jaar op ${day} ${name}` : `Elk jaar${on}`
  }
  return `Elke maand${on}`
}

export async function loadRules(): Promise<RecurringRule[]> {
  const { data, error } = await supabase
    .from('recurring_rules')
    .select('id, space_id, party_id, description, category, rrule, starts_on, ends_on, mode, amount_cents, iban, payment_reference, collection_method, active, last_materialised_on')
    .is('deleted_at', null)
    .order('active', { ascending: false })
    .order('description')
  if (error) throw error

  return (data ?? []).map((r) => ({
    id: r.id as string,
    spaceId: r.space_id as string,
    partyId: (r.party_id as string | null) ?? null,
    description: r.description as string,
    category: (r.category as string | null) ?? null,
    rrule: r.rrule as string,
    startsOn: r.starts_on as string,
    endsOn: (r.ends_on as string | null) ?? null,
    mode: r.mode as AmountMode,
    amountCents: Number(r.amount_cents ?? 0),
    iban: (r.iban as string | null) ?? null,
    paymentReference: (r.payment_reference as string | null) ?? null,
    collectionMethod: (r.collection_method as CollectionMethod) ?? 'manual',
    active: Boolean(r.active),
    lastMaterialisedOn: (r.last_materialised_on as string | null) ?? null,
  }))
}

export interface NewRule {
  spaceId: string
  partyId: string | null
  description: string
  category: string | null
  rrule: string
  startsOn: string
  /** Null means no end — but only ever because someone chose that (§10). */
  endsOn: string | null
  mode: AmountMode
  amountCents: number
  iban: string | null
  paymentReference: string | null
  /** One of the database's own values — never the word "transfer" (0022). */
  collectionMethod: CollectionMethod
  userId: string
}

export async function createRule(rule: NewRule): Promise<string> {
  // Rule 14, a step earlier: an account number that fails mod-97 never gets
  // stored, so it can never reach a QR code. A variable rule carries none at
  // all — the check constraint in 0016 enforces that too.
  const iban = rule.mode === 'fixed' && rule.iban ? rule.iban.replace(/\s/g, '').toUpperCase() : null
  if (iban && !validateIban(iban).valid) {
    throw new Error('Dat rekeningnummer klopt niet.')
  }

  const { data, error } = await supabase
    .from('recurring_rules')
    .insert({
      space_id: rule.spaceId,
      party_id: rule.partyId,
      description: rule.description,
      category: rule.category,
      rrule: rule.rrule,
      starts_on: rule.startsOn,
      ends_on: rule.endsOn,
      ends_on_set: true,
      mode: rule.mode,
      amount_cents: rule.amountCents,
      iban,
      payment_reference: rule.mode === 'fixed' ? rule.paymentReference : null,
      collection_method: rule.collectionMethod,
      created_by: rule.userId,
    })
    .select('id')
    .single()
  if (error) throw error

  await supabase.from('activity_log').insert({
    space_id: rule.spaceId,
    actor_id: rule.userId,
    action: 'recurring.created',
    entity_type: 'recurring_rule',
    entity_id: data.id,
    detail: { rrule: rule.rrule, mode: rule.mode, amount_cents: rule.amountCents },
  })

  return data.id as string
}

/**
 * Stopping a rule leaves what it already put in the calendar alone.
 *
 * Deleting the future rows would be the tidier-looking choice and the wrong
 * one: some of them may already be planned, or paid, or attached to an
 * invoice. Stopping means "make no more", not "pretend there were none".
 */
export async function stopRule(rule: RecurringRule, _userId?: string): Promise<void> {
  // One statement. This used to be three — deactivate, wipe the future
  // estimates, log — and dying after the first left estimates sitting in the
  // calendar for a bill that is no longer coming, with no rule to explain them
  // and nothing on record saying anyone stopped anything. See migration 0045.
  //
  // Which rows go is decided in the database, not here: only estimates, only
  // future ones. Everything else may already be planned, paid, or attached to
  // an invoice. Stopping means "make no more", not "pretend there were none".
  const { error } = await supabase.rpc('stop_recurring_rule', { p_rule_id: rule.id })
  if (error) throw error
}
